import { Alert, Box, Stack, Typography } from '@mui/material';

const severityOrder = { error: 0, warning: 1, info: 2 };

const sortedWarnings = (warnings = []) =>
  [...warnings].sort(
    (left, right) => (severityOrder[left.severity] ?? 3) - (severityOrder[right.severity] ?? 3)
  );

const number = (value) => Number(value) || 0;

const CountRow = ({ label, value, detail = '' }) => (
  <Box display="flex" justifyContent="space-between" alignItems="baseline" gap={2}>
    <Typography variant="body2" color="text.secondary">
      {label}
    </Typography>
    <Typography component="span" variant="body2" fontWeight={600} textAlign="right">
      {value}
      {detail ? (
        <Typography component="span" variant="caption" color="text.secondary">
          {detail}
        </Typography>
      ) : null}
    </Typography>
  </Box>
);

const CountSection = ({ title, rows }) => {
  const visible = rows.filter((row) => number(row.value) > 0 || row.always);
  if (visible.length === 0) {
    return null;
  }
  return (
    <Box>
      <Typography variant="subtitle2" fontWeight={600} gutterBottom>
        {title}
      </Typography>
      <Stack gap={0.5}>
        {visible.map((row) => (
          <CountRow key={row.label} label={row.label} value={row.value} detail={row.detail} />
        ))}
      </Stack>
    </Box>
  );
};

/**
 * Read-only summary of a restore preview or the result of applying one. The
 * server builds the counts and warnings; this component only lays them out.
 * Nothing here writes.
 */
const EventSnapshotPreview = ({ result, applied = false }) => {
  if (!result) {
    return null;
  }

  const counts = result.counts || {};
  const eventCounts = counts.event || {};
  const gameCounts = counts.games || {};
  const bracketCounts = counts.brackets || {};
  const warnings = sortedWarnings(result.warnings);
  const errors = applied ? [] : result.errors || [];
  const written = result.written || null;

  const eventRows = [
    { label: 'Event fields changed', value: eventCounts.changedFields },
    { label: 'Sections added', value: eventCounts.sections?.added },
    { label: 'Sections removed', value: eventCounts.sections?.removed },
    { label: 'Sections changed', value: eventCounts.sections?.changed },
    { label: 'Teams added', value: eventCounts.teams?.added },
    { label: 'Teams removed', value: eventCounts.teams?.removed },
    { label: 'Teams changed', value: eventCounts.teams?.changed },
    { label: 'Players added', value: eventCounts.players?.added },
    { label: 'Players removed', value: eventCounts.players?.removed },
    { label: 'Players changed', value: eventCounts.players?.changed }
  ];

  const gameRows = [
    { label: 'Games updated', value: gameCounts.updated },
    { label: 'Games recreated', value: gameCounts.recreated },
    { label: 'Games hidden (soft-deleted)', value: gameCounts.softDeleted },
    { label: 'Games unchanged', value: gameCounts.unchanged, always: true }
  ];

  const bracketRows = [
    { label: 'Brackets updated', value: bracketCounts.updated },
    { label: 'Brackets recreated', value: bracketCounts.recreated },
    { label: 'Brackets deleted', value: bracketCounts.deleted },
    { label: 'Brackets unchanged', value: bracketCounts.unchanged, always: true }
  ];

  const writtenRows = written
    ? [
        { label: 'Games written', value: written.games },
        { label: 'Bracket documents written', value: written.bracketDocuments },
        { label: 'Event document written', value: written.eventDocument }
      ]
    : [];

  return (
    <Stack gap={2}>
      {applied ? (
        <Alert severity="success">
          {result.restoredSnapshot?.name
            ? `Restored "${result.restoredSnapshot.name}".`
            : 'The event was restored from this point.'}
        </Alert>
      ) : result.alreadyApplied ? (
        <Alert severity="info">
          This restore point already matches the event. Nothing will be written.
        </Alert>
      ) : null}

      <CountSection title="Event and roster" rows={eventRows} />
      <CountSection title="Games and results" rows={gameRows} />
      <CountSection title="Brackets" rows={bracketRows} />
      {applied ? <CountSection title="Written" rows={writtenRows} /> : null}

      {errors.map((error) => (
        <Alert key={error.code || error.message} severity="error">
          {error.message || 'This restore point cannot be applied.'}
        </Alert>
      ))}

      {warnings.map((item) => (
        <Alert key={item.code || item.message} severity={item.severity || 'warning'}>
          {item.message}
        </Alert>
      ))}
    </Stack>
  );
};

export default EventSnapshotPreview;
